import type { GetMeetingsParams } from '@repo/api/meeting/meetingService';

import { useGetAssignedMeetingsInfinite } from '@repo/api/meeting/useGetAssignedMeetingsInfinite';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { cn } from '@repo/lib/cn';
import { endOfDay, startOfDay } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

import MeetingsList from '@/components/features/calendar/MeetingsList';
import MeetingsListContent from '@/components/features/calendar/MeetingsListContent';
import MeetingsListHeader from '@/components/features/calendar/MeetingsListHeader';
import GroupElementHeader from '@/components/features/groups/layout/GroupElementHeader';
import { Button } from '@/components/ui/button';

interface HomeCalendarScheduleProps {
  mode?: 'desktop' | 'mobile';
}

export default function HomeCalendarSchedule(homeCalendarScheduleProps: HomeCalendarScheduleProps) {
  const { mode = 'desktop' } = homeCalendarScheduleProps;
  if (mode === 'mobile') {
    return <HomeCalendarScheduleMobile />;
  }
  return <HomeCalendarScheduleDesktop />;
}

function HomeCalendarScheduleMobile() {
  const query = useGetAssignedMeetingsInfinite({
    after: startOfDay(new Date()).toISOString(),
    sortOrder: 'ASC',
    pageSize: 2,
  });

  const { data: meetings = [] } = useInfiniteQueryResult(query.data);

  return (
    <div className={'border-border-primary bg-background flex flex-col rounded-2xl border'}>
      <GroupElementHeader label={'Nadchodzące spotkania'} />
      <MeetingsList meetings={meetings} query={query} disableInfiniteScroll>
        <MeetingsListContent />
      </MeetingsList>
    </div>
  );
}

function HomeCalendarScheduleDesktop() {
  const [queryParams, setQueryParams] = useState<GetMeetingsParams>({
    after: startOfDay(new Date()).toISOString(),
    before: endOfDay(new Date()).toISOString(),
    sortOrder: 'ASC',
    page: 0,
    pageSize: 15,
  });
  const [date, setDate] = useState<Date>(new Date());
  const [type, setType] = useState<'schedule' | 'day'>('day');

  const query = useGetAssignedMeetingsInfinite(queryParams);
  const { data: meetings = [] } = useInfiniteQueryResult(query.data);

  const handleSetDate = (day: Date) => {
    setType('day');
    setQueryParams((curr) => ({
      ...curr,
      after: startOfDay(day).toISOString(),
      before: endOfDay(day).toISOString(),
      page: 0,
    }));
    setDate(day);
  };

  const handleSetSchedule = () => {
    setType('schedule');
    setQueryParams((curr) => ({
      ...curr,
      before: endOfDay(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)).toISOString(),
      after: startOfDay(new Date()).toISOString(),
      page: 0,
    }));
    setDate(new Date());
  };

  return (
    <MeetingsList meetings={meetings} query={query}>
      <div className={cn('flex flex-col gap-4', 'viewport-height sticky top-4 min-h-84 w-86 max-w-86')}>
        <div className={cn('bg-background flex flex-col gap-4 overflow-hidden rounded-2xl border py-4', type === 'schedule' ? 'shrink-0' : 'grow')}>
          <MeetingsListHeader date={date} setDate={handleSetDate} />
          <div className={cn('flex h-full grow flex-col gap-4 overflow-hidden', type === 'schedule' && 'hidden')}>
            <MeetingsListContent />
            <div className={'flex w-full justify-end px-4'}>
              <Button size={'sm'} variant={'ghost'} onClick={handleSetSchedule}>
                <ArrowLeft />
                Do nadchodzących
              </Button>
            </div>
          </div>
        </div>
        <div className={cn('bg-background flex grow flex-col gap-5 overflow-hidden rounded-2xl border py-4', type === 'day' && 'hidden')}>
          <div className={'px-4'}>
            <h5 className={'text-muted-foreground font-medium'}>Nadchodzące spotkania</h5>
          </div>
          <MeetingsListContent />
        </div>
      </div>
    </MeetingsList>
  );
}
